/**
 * Important note: This component is way different from the other charts supported on the platform. While
 * Highcharts does appear to have a "stacked bars" chart available (https://www.highcharts.com/demo/bar-stacked),
 * it's extremely far from the result we're looking for here, and it seems like a huge time sink to use that as a
 * starting point.
 *
 * Current assumptions:
 * - The stacked bar will have at max 2 categories. Should be able to support more than 2 with a little effort.
 * - The chart does not require any sort of interactions - it's in a read-only state.
 *
 * If any of those assumptions become invalid, we'll have to strongly reconsider the simplistic approach used
 * by this component, and potentially go for a charts library that supports this type of chart.
 */

// Third-party
import React from 'react';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy Component
import LegacyStackedBar from './LegacyStackedBar';

// Types
import Nullable from '@/types/nullable';

export type StackedBarSliceData = {
  value: number;
  color: string;
};

export type StackedBarData = {
  title?: string;
  slices: StackedBarSliceData[];
  legend?: Nullable<{ color: string; label: string }[]>;
  bounds?: {
    lower: React.ReactNode;
    upper: React.ReactNode;
  };
};

interface Props {
  data: StackedBarData;
  showLabels?: boolean;
}

const StackedBarV2 = ({ data, showLabels = true }: Props) => {
  const getStyles = (slice: StackedBarSliceData) => ({
    width: `${slice.value}%`,
    backgroundColor: slice.color,
  });

  return (
    <div className="stacked-bar-wrapper">
      {data.title && (
        <div className="labels">
          <span>{data.title}</span>
        </div>
      )}

      {data.bounds && showLabels && (
        <div className="labels">
          <span>{data.bounds.lower}</span>
          <span>{data.bounds.upper}</span>
        </div>
      )}

      <div className="stacked-bar-v2">
        {data.slices
          .filter(slice => slice.value !== 0)
          .map((slice, index) => (
            <div key={index} style={getStyles(slice)} className="bar-section" />
          ))}
      </div>
      {showLabels && data.legend && (
        <div className="stacked-bar-legend">
          {data.legend.map((legend, index) => (
            <div key={index} className="stacked-bar-legend-item">
              <div className="stacked-bar-legend-color" style={{ backgroundColor: legend.color }} />
              <span className="stacked-bar-legend-label">{legend.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StackedBar = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <StackedBarV2 {...props} />;
  }
  return <LegacyStackedBar {...props} />;
};

export default StackedBar;
